import { useId, type ReactNode, type Ref } from 'react';
import { SimpleGrid, TextInput } from '@mantine/core';
import type { GetInputPropsReturnType } from '@mantine/form';
import { MEDLEY_EMAIL_LABEL, PERSONAL_EMAIL_LABEL } from '../../utils/medleyEmail';

/**
 * MED-1355: the two coach email fields, in one order and with one look for
 * every coach form (Add Coach and the coach profile edit).
 *
 * - Personal Email comes first and shows the required mark.
 * - Each field has a slot under the input. The slot shows the hint, or the
 *   error in its place. The slot keeps a fixed minimum height, so an
 *   error that comes or goes does not move the fields or the buttons below.
 * - The fields use withAsterisk, not the native required attribute, so the
 *   browser never shows its own "Please fill out this field" bubble. The
 *   form validation shows every error under its field.
 * - The slot is a polite live region at all times, so a screen reader
 *   reads an error when it comes into the slot.
 * - The slot is the Mantine description, below the input. Mantine sets
 *   aria-describedby on the input from its own context and ignores a prop,
 *   so the description is how the input points to the slot.
 */

// Room for two lines of xs text. Most errors fit, so the layout does not
// move when an error comes or goes. Three lines left a large empty gap
// under the hint in normal use (MED-1355 QA round 2).
const SLOT_MIN_HEIGHT = 'calc(2 * var(--mantine-line-height-xs) * var(--mantine-font-size-xs))';

interface CoachEmailFieldProps {
  label: string;
  placeholder: string;
  hint: string;
  /** Shown in place of the hint, in the warning colour, when there is no error. */
  warning?: string;
  required?: boolean;
  labelClassName?: string;
  inputProps: GetInputPropsReturnType;
  inputRef?: Ref<HTMLInputElement>;
}

function CoachEmailField({
  label,
  placeholder,
  hint,
  warning,
  required = false,
  labelClassName,
  inputProps,
  inputRef,
}: CoachEmailFieldProps) {
  const slotId = useId();
  const { error, ...rest } = inputProps;
  const message: ReactNode = error || warning || hint;
  return (
    <TextInput
      {...rest}
      ref={inputRef}
      label={label}
      placeholder={placeholder}
      withAsterisk={required}
      aria-required={required || undefined}
      // A boolean error gives the red border, but Mantine renders no error
      // text and adds no error id. The slot below shows the message.
      error={Boolean(error)}
      description={message}
      inputWrapperOrder={['label', 'input', 'description']}
      descriptionProps={{ id: slotId, 'aria-live': 'polite' }}
      classNames={labelClassName ? { label: labelClassName } : undefined}
      styles={{
        description: {
          marginTop: 4,
          minHeight: SLOT_MIN_HEIGHT,
          fontSize: 'var(--mantine-font-size-xs)',
          lineHeight: 'var(--mantine-line-height-xs)',
          color: error
            ? 'var(--medley-red-danger)'
            : warning
              ? 'var(--medley-text-warning)'
              : 'var(--medley-text-secondary)',
        },
      }}
    />
  );
}

interface CoachEmailFieldsProps {
  personalInputProps: GetInputPropsReturnType;
  medleyInputProps: GetInputPropsReturnType;
  /** Optional class for the field labels, to match the page's own labels. */
  labelClassName?: string;
  /** Optional ref to the Medley Email input, so a page can focus it. */
  medleyInputRef?: Ref<HTMLInputElement>;
  /**
   * MED-1355 QA round 5: a warning under Personal Email, for example when an
   * older Medley address is still stored there.
   */
  personalWarning?: string;
}

export function CoachEmailFields({
  personalInputProps,
  medleyInputProps,
  labelClassName,
  medleyInputRef,
  personalWarning,
}: CoachEmailFieldsProps) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="xs">
      <CoachEmailField
        label={PERSONAL_EMAIL_LABEL}
        placeholder="e.g., sarah@leadershiplab.co"
        hint="Not a withmedley.com address."
        warning={personalWarning}
        required
        labelClassName={labelClassName}
        inputProps={personalInputProps}
      />
      <CoachEmailField
        label={MEDLEY_EMAIL_LABEL}
        placeholder="e.g., sarah.chen@withmedley.com"
        hint="Needed before the coach can lead a group or coach a 1:1."
        labelClassName={labelClassName}
        inputProps={medleyInputProps}
        inputRef={medleyInputRef}
      />
    </SimpleGrid>
  );
}

export default CoachEmailFields;
